import { RARITY_ORDER, type InventoryItem, type ItemRarity } from '../net/protocol';
import { affixLine } from './compare';

/**
 * Searching, filtering and sorting a thousand stored items.
 *
 * ── Why this is a module and not a component ─────────────────────────────────
 *
 * Every rule about *which* items a query picks out, and *what order* they come back in,
 * is a decision somebody has to be able to argue with — so it is pure, exported, and
 * tested without rendering anything. The panel keeps the inputs and the list; this keeps
 * the meaning. The same split `drag.ts` uses, for the same reason.
 *
 * ── Why the vault gets its own query and the bag keeps `StashFilter` ─────────
 *
 * They are different questions. `stash.ts` filters what a player is *carrying* — a
 * dozen items, where the question is "which of these is worth keeping" and the answer
 * dims non-matches rather than hiding them, because the grid's geometry is the point.
 *
 * The vault holds up to a thousand, where the question is "where is the ring with fire
 * resistance" and the answer must *hide* everything else — a list of a thousand rows
 * with fifteen of them undimmed is not a search result. So this returns a filtered,
 * ordered array where `matchesFilter` returns a boolean, and it takes the extra fields
 * a large archive needs: an item-level band, a slot, a lock state, and an order.
 *
 * `compare.ts`'s `affixLine` is reused rather than re-derived, because the searchable text
 * of an affix must be the same string the tooltip shows — a search that misses what the
 * player can read is worse than no search.
 */

/** What to order the list by. */
export type VaultSort =
  /** Alphabetical, which is the only order a player can predict. */
  | 'NAME'
  | 'ITEM_LEVEL'
  | 'RARITY'
  /** The doll slot: all the helmets together, then all the gloves. */
  | 'TYPE'
  /** The order the server sent, which is the order things were stored. */
  | 'STORED';

export const VAULT_SORTS: readonly VaultSort[] = ['STORED', 'NAME', 'ITEM_LEVEL', 'RARITY', 'TYPE'];

/** Human labels for the sort keys, so the panel does not spell them itself. */
export const VAULT_SORT_LABELS: Record<VaultSort, string> = {
  STORED: 'Stored',
  NAME: 'Name',
  ITEM_LEVEL: 'Item level',
  RARITY: 'Rarity',
  TYPE: 'Type',
};

export type SortDirection = 'ASC' | 'DESC';

/**
 * Everything the player has narrowed the vault by.
 *
 * One object rather than several signals, so `applyQuery` has a single argument and a
 * test can state a whole query as a literal. Every field has an "unset" that means *do
 * not narrow by this*, and none of them is `undefined`: an absent key and an empty one
 * would be two ways to say the same thing, and the panel would eventually produce both.
 */
export interface VaultQuery {
  /** Free text over name, base id, slot and affix text. Empty matches everything. */
  text: string;
  /**
   * The rarities to keep. **Empty means all of them**, not none — the filter chips are
   * off by default and an empty set has to mean "no opinion", or opening the panel
   * would show an empty vault.
   */
  rarities: readonly ItemRarity[];
  /** The doll slots to keep. Empty means all. */
  slots: readonly string[];
  /** Item-level band, inclusive. Null at either end means unbounded. */
  minLevel: number | null;
  maxLevel: number | null;
  /** Show only the padlocked items. The lock is the player's own "keep this" mark. */
  lockedOnly: boolean;
  sort: VaultSort;
  direction: SortDirection;
}

/**
 * The query a freshly-opened vault has: no narrowing, newest-stored first.
 *
 * `STORED` / `DESC` rather than alphabetical, deliberately. The thing a player wants to
 * see the moment they open the vault after a dive is **what they just put in it**, and
 * an alphabetical default buries it. Sorting by name is one click away.
 */
export const EMPTY_QUERY: VaultQuery = {
  text: '',
  rarities: [],
  slots: [],
  minLevel: null,
  maxLevel: null,
  lockedOnly: false,
  sort: 'STORED',
  direction: 'DESC',
};

/** Whether a query narrows anything at all — for the "clear filters" affordance. */
export function isNarrowed(query: VaultQuery): boolean {
  return (
    query.text.trim() !== '' ||
    query.rarities.length > 0 ||
    query.slots.length > 0 ||
    query.minLevel !== null ||
    query.maxLevel !== null ||
    query.lockedOnly
  );
}

/**
 * Every word in the query must appear somewhere in the item's text.
 *
 * Words rather than the whole string, because `"fire ring"` is how a person searches
 * for a ring with fire resistance and a substring match on the whole phrase finds
 * nothing — the two words are in different fields. Each word may match a *different*
 * field, which is the behaviour that makes the box feel like it works.
 */
function matchesText(item: InventoryItem, text: string): boolean {
  const words = text.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;

  const haystack = [
    item.name,
    item.baseId,
    item.slot,
    item.rarity,
    ...item.affixes.map((a) => affixLine(a)),
    item.power ?? '',
  ]
    .join(' ')
    .toLowerCase();

  return words.every((word) => haystack.includes(word));
}

/** Whether one item survives a query. Every clause narrows; none widens. */
export function matchesQuery(item: InventoryItem, query: VaultQuery): boolean {
  if (query.lockedOnly && !item.isLocked) return false;
  if (query.rarities.length > 0 && !query.rarities.includes(item.rarity)) return false;
  if (query.slots.length > 0 && !query.slots.includes(item.slot)) return false;
  if (query.minLevel !== null && item.itemLevel < query.minLevel) return false;
  if (query.maxLevel !== null && item.itemLevel > query.maxLevel) return false;
  return matchesText(item, query.text);
}

/**
 * Compares two items on the sort key, ascending, before the direction is applied.
 *
 * **Every comparison ends in a tiebreak that cannot tie**, and that is not tidiness:
 * `Array.prototype.sort` is only stable within one call, so two items that compare
 * equal can swap places when an *unrelated* item is added or removed. In a list of a
 * thousand rows that reads as the vault shuffling itself for no reason. Name then uuid
 * settles it — uuid last, because it is the only field guaranteed unique.
 */
function compareBy(a: InventoryItem, b: InventoryItem, sort: VaultSort): number {
  switch (sort) {
    case 'NAME':
      break;
    case 'ITEM_LEVEL':
      if (a.itemLevel !== b.itemLevel) return a.itemLevel - b.itemLevel;
      break;
    case 'RARITY':
      {
        const rank = RARITY_ORDER.indexOf(a.rarity) - RARITY_ORDER.indexOf(b.rarity);
        if (rank !== 0) return rank;
      }
      break;
    case 'TYPE':
      {
        const slot = a.slot.localeCompare(b.slot);
        if (slot !== 0) return slot;
      }
      break;
    case 'STORED':
      // Nothing to compare: the array already *is* stored order. Returning zero and
      // letting the stable tiebreak below run would reorder it alphabetically, which is
      // the one thing this key must not do — so `applyQuery` never calls this for
      // `STORED`. Kept as a case so the switch stays exhaustive and a new key cannot be
      // added without deciding what it means.
      return 0;
  }

  const name = a.name.localeCompare(b.name);
  if (name !== 0) return name;
  return a.uuid.localeCompare(b.uuid);
}

/**
 * The items to show, filtered and ordered.
 *
 * A new array every call — the input is the server's frame and must not be mutated, and
 * `sort` sorts in place. The panel memoises the result, so this runs when the query or
 * the frame changes rather than per render.
 */
export function applyQuery(
  items: readonly InventoryItem[],
  query: VaultQuery,
): InventoryItem[] {
  const kept = items.filter((item) => matchesQuery(item, query));

  // `STORED` is the server's order, so ascending is the array as it arrived and
  // descending is it reversed. Sorting is skipped entirely rather than done with a
  // comparator that returns zero: see the note in `compareBy`.
  if (query.sort === 'STORED') {
    return query.direction === 'ASC' ? kept : kept.reverse();
  }

  const sign = query.direction === 'ASC' ? 1 : -1;
  return kept.sort((a, b) => sign * compareBy(a, b, query.sort));
}

/**
 * Which rarities and slots are actually in the vault.
 *
 * The filter chips are built from this rather than from the full vocabulary, because a
 * chip for a slot the player owns nothing in is a control that can only ever empty the
 * list. Both come back in a fixed order — rarity by rank, slots alphabetical — so the
 * chips do not reorder themselves as the vault changes.
 */
export function vaultFacets(items: readonly InventoryItem[]): {
  rarities: ItemRarity[];
  slots: string[];
} {
  const rarities = new Set<ItemRarity>();
  const slots = new Set<string>();
  for (const item of items) {
    rarities.add(item.rarity);
    slots.add(item.slot);
  }
  return {
    rarities: RARITY_ORDER.filter((r) => rarities.has(r)),
    slots: [...slots].sort((a, b) => a.localeCompare(b)),
  };
}

/**
 * Toggles one value in a filter list, returning a new list.
 *
 * Here rather than in the panel because "clicking a chip twice clears it" is a rule,
 * and a panel that re-implemented it per filter would eventually have one that only
 * turns on. Generic over the element type so the rarity and slot chips share it.
 */
export function toggleFilter<T>(current: readonly T[], value: T): T[] {
  return current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
}
